import type { Lease, LeasePowerAction, LeaseState, Role, Vm, VmState } from '../api/types'
import { DASH, size, sizeMb } from '../ui/format'
import { splitTags } from './team'

// 임대와 VM 의 상태 기계를 화면의 낱말로. 원 enum 은 화면에 나오지 않는다.
// 상태 색은 셋뿐이다(정상 · 경고 · 위험). 진행 중인 것은 색이 없다 — 진행은 상태가 아니다.

export type Tone = 'ok' | 'warn' | 'danger' | 'none'

export const leaseLabel: Record<LeaseState, string> = {
  requested: '요청 중',
  pending_approval: '승인 대기',
  rejected: '반려',
  active: '사용 중',
  ending: '반납 중',
  retired: '보존 중',
  ended: '반납 완료',
  failed: '실패',
}

export const leaseTone: Record<LeaseState, Tone> = {
  requested: 'none',
  pending_approval: 'warn',
  rejected: 'none',
  active: 'ok',
  ending: 'none',
  retired: 'warn',
  ended: 'none',
  failed: 'danger',
}

export const vmLabel: Record<VmState, string> = {
  planned: '자리 배정',
  provisioning: '만드는 중',
  running: '실행 중',
  stopped: '정지',
  destroying: '삭제 중',
  destroyed: '삭제됨',
  error: '오류',
}

export const vmTone: Record<VmState, Tone> = {
  planned: 'none',
  provisioning: 'none',
  running: 'ok',
  stopped: 'warn',
  destroying: 'none',
  destroyed: 'none',
  error: 'danger',
}

/** 상태 낱말. 사전에 없는 값은 원문이 아니라 대시다 — 원 enum 은 화면에 나오지 않는다. */
export function leaseWord(state: string): string {
  return (leaseLabel as Record<string, string>)[state] ?? DASH
}
export function leaseToneOf(state: string): Tone {
  return (leaseTone as Record<string, Tone>)[state] ?? 'none'
}
export function vmWord(state: string): string {
  return (vmLabel as Record<string, string>)[state] ?? DASH
}
export function vmToneOf(state: string): Tone {
  return (vmTone as Record<string, Tone>)[state] ?? 'none'
}

/** 닿지 않음 — 기계의 노드가 응답하지 않는 동안(B-23). 상태 원값이 아니라 `reachable` 이 정한다. 경고 색이다. */
export const UNREACHABLE_WORD = '닿지 않음'
export function isUnreachable(vm: Vm | null | undefined): boolean {
  return !!vm && vm.reachable === false
}

/** 아직 살아 있는 VM — 사용자의 "VM 수" 와 쿼터가 세는 것. 묘비는 세지 않는다. */
export function isLive(vm: Vm): boolean {
  return vm.state !== 'destroyed' && vm.state !== 'error'
}

/** 관리자 승인을 기다리는 신청. */
export function isPending(l: Lease): boolean {
  return l.state === 'pending_approval'
}

/** VM 하나가 가질 수 있는 태그 수 — 서버의 `LeaseCreate.tags`·`LeaseTags.tags` 의 maxItems 와 같다(F-95). */
export const LEASE_TAG_LIMIT = 10
/** 그 수를 넘었을 때 태그 칸 아래의 막힘 문장. */
export const LEASE_TAG_LIMIT_RULE = `태그는 ${LEASE_TAG_LIMIT}개까지 붙일 수 있습니다.`
/** 태그 칸의 글자가 보낼 수 없을 만큼 많은 태그인가 — 칸은 `splitTags` 로 읽는다. */
export function leaseTagsOver(raw: string): boolean {
  return splitTags(raw).length > LEASE_TAG_LIMIT
}

/**
 * 내가 전에 쓴 태그 가운데 지금 칸에 없는 것 — 태그 칸 아래의 제안 칩이다. 많이 쓴 것이 앞이고 같으면 가나다순이다.
 * 태그는 소유자의 낱말이라 남의 VM 의 태그는 제안하지 않는다(부르는 쪽이 내 임대만 넘긴다).
 */
export function pastTags(mine: readonly { tags: readonly string[] }[], current: readonly string[]): string[] {
  const count = new Map<string, number>()
  for (const l of mine) for (const t of l.tags) count.set(t, (count.get(t) ?? 0) + 1)
  const have = new Set(current)
  return [...count.entries()]
    .filter(([t]) => !have.has(t))
    .sort(([a, x], [b, y]) => y - x || a.localeCompare(b))
    .map(([t]) => t)
}

/**
 * 태그를 고칠 수 있는가 — 소유자 하나다. 태그는 소유자가 VM 을 부르는 말이라 최고 관리자도 남의 VM 에는 보기만 하고,
 * 반납된 자기 VM 도 고친다(그 VM 이 무엇이었는지의 기록이다). 서버의 문(`PUT /leases/{id}/tags`)과 같은 판정이다.
 */
export function mayTag(l: { owner: { id: string } }, viewer: { id: string } | null): boolean {
  return viewer !== null && l.owner.id === viewer.id
}

/** 끝난 임대 — 목록에서 뒤로 보낸다. */
export function isClosed(l: Lease): boolean {
  return l.state === 'retired' || l.state === 'ended' || l.state === 'rejected' || l.state === 'failed'
}

/**
 * 전원 조작의 낱말과 조건. 실행은 정지된 기계에만, 나머지는 실행 중인 기계에만 선다 — 어느 쪽인지는 `vm.state` 다.
 * 실행만 묻지 않고 나머지는 제목 하나와 버튼 둘의 대화 상자를 거친다. 종료·재부팅은 게스트에게 묻고, 정지·리셋은 묻지 않는다.
 * 사용자 VM 상세의 칩과 관리자 VM 목록의 일괄 조작이 이 한 표를 읽는다.
 */
export type PowerChip = { action: LeasePowerAction; word: string; when: 'running' | 'stopped'; confirm: boolean }
export const POWER: readonly PowerChip[] = [
  { action: 'start', word: '실행', when: 'stopped', confirm: false },
  { action: 'shutdown', word: '종료', when: 'running', confirm: true },
  { action: 'stop', word: '정지', when: 'running', confirm: true },
  { action: 'reboot', word: '재부팅', when: 'running', confirm: true },
  { action: 'reset', word: '리셋', when: 'running', confirm: true },
]

/** VM 에 하는 조작 — 전원과 반납. 반납은 되돌릴 수 없어 늘 묻는다. */
export type VmAction = LeasePowerAction | 'end'
const VM_ACTIONS: readonly VmAction[] = [...POWER.map((p) => p.action), 'end']

/**
 * 이 VM 을 바꿀 수 있는가 — 소유자와 최고 관리자뿐이다. 관리자는 자기 팀 사람의 VM 을 보고 키를 받고 콘솔로 들어가기만 한다:
 * 백엔드가 VM 을 바꾸는 조작(전원·반납·디스크·변경 신청과 취소·키 재발급·앱 공개)을 소유자와 최고 관리자에게서만 받으므로,
 * 관리자의 칩은 눌리는 순간 거절된다.
 */
export function mayChange(l: { owner: { id: string } }, viewer: { id: string; role: Role } | null): boolean {
  return viewer !== null && (viewer.role === 'superadmin' || l.owner.id === viewer.id)
}

/**
 * 보는 사람이 이 VM 에 할 수 있는 조작. 상태 조건은 사용자 VM 상세의 칩과 같다 — 사용 중인 임대만이고,
 * 전원은 그 상태의 것이며 노드가 응답하지 않는 동안은 없다. 남의 VM 은 `mayChange` 가 가른다.
 */
export function vmActions(row: { lease: Lease; vm: Vm | null }, viewer: { id: string; role: Role }): VmAction[] {
  const l = row.lease
  if (l.state !== 'active') return []
  if (!mayChange(l, viewer)) return []
  const v = row.vm
  const power = v && !isUnreachable(v) ? POWER.filter((p) => p.when === v.state).map((p) => p.action) : []
  return [...power, 'end']
}

/** 고른 VM 모두가 허락하는 조작. 하나라도 허락하지 않으면 그 칩은 서지 않는다. */
export function vmBatchActions(rows: { lease: Lease; vm: Vm | null }[], viewer: { id: string; role: Role }): VmAction[] {
  if (rows.length === 0) return []
  const each = rows.map((r) => vmActions(r, viewer))
  return VM_ACTIONS.filter((a) => each.every((acts) => acts.includes(a)))
}

/**
 * 임대의 디스크 할당(GB) — 루트와 데이터 볼륨의 합이고, 쿼터가 이 임대에서 세는 몫과 같다(`disk_allocated_gb`).
 * 게스트가 쓴 양이 아니다. 목록의 DISK 칸과 그 정렬, 상태 창의 `할당` 이 이 하나를 읽는다.
 * 필드가 없는 응답(배포 중 새 화면이 옛 백엔드를 만난 때)은 루트와 살아 있는 VM 에 붙은 데이터 디스크로 센다 —
 * 아직 디스크가 되지 않은 신청 볼륨만 빠진다.
 */
export function diskAllocatedGb(l: Lease): number {
  if (typeof l.disk_allocated_gb === 'number') return l.disk_allocated_gb
  const disks = l.vm && isLive(l.vm) ? l.vm.disks : []
  return disks.reduce((gb, d) => (d.is_boot ? gb : gb + d.size_gb), l.disk_gb)
}

/** 요청 자원 한 줄. `4 코어 · 16GB · 100GB · GPU 2장 A16`. GPU 0 이면 GPU 를 말하지 않는다. */
export function requestText(l: Lease): string {
  const parts = [`${l.cores}코어`, sizeMb(l.memory_mb), size(l.disk_gb)]
  if (l.gpus > 0) parts.push(`GPU ${l.gpus}장${l.gpu_model ? ` ${l.gpu_model}` : ''}`)
  return parts.join(' · ')
}

export function vmSpecText(vm: Vm): string {
  const parts = [`${vm.cores}코어`, sizeMb(vm.memory_mb), size(vm.disk_gb)]
  if (vm.gpus.length > 0) parts.push(`GPU ${vm.gpus.length}장`)
  return parts.join(' · ')
}

/** GPU 부착을 모델별로. `A16 2장`. 모델을 모르면 장 수만. */
export function vmGpuText(vm: Vm): string {
  if (vm.gpus.length === 0) return 'GPU 없음'
  const by = new Map<string, number>()
  for (const g of vm.gpus) {
    const k = g.model ?? ''
    by.set(k, (by.get(k) ?? 0) + 1)
  }
  return [...by.entries()].map(([m, n]) => (m ? `${m} ${n}장` : `${n}장`)).join(' · ')
}

/** 접속 한 줄. 주소가 없으면 null — 없는 값을 그리지 않는다. */
export function accessText(l: Lease): string | null {
  if (!l.access?.ipv4) return null
  return `${l.access.ssh_user}@${l.access.ipv4}:${l.access.ssh_port}`
}

const DISK_WORD: Record<string, string> = {
  attaching: '추가 중', attached: '연결됨', resizing: '확장 중', detaching: '분리 중', detached: '삭제 중',
}
export function diskWord(state: string): string { return DISK_WORD[state] ?? '—' }

/** 보존 중 임대의 조작에 필요한 것. VM 상세는 임대에서, 검토 대기는 항목에서 채운다. */
export type Retained = {
  id: string
  name: string
  gpus: number
  purgeAfter: string | null | undefined
  /** 파기가 진행 중이면 어느 조작도 없다. */
  purging: boolean
}

export function retainedOf(lease: Lease): Retained | null {
  if (lease.state !== 'retired') return null
  return { id: lease.id, name: lease.name, gpus: lease.gpus, purgeAfter: lease.purge_after, purging: lease.vm?.state === 'destroying' }
}

// 변경(B-27)의 낱말. 승인된 변경은 사용자가 재부팅할 때 적용되므로(B-29) 대기와 적용 중 사이에
// `재부팅 대기` 가 하나 더 있다. 끝난 변경의 실패 이유는 코드마다 한 문장이다.
const changeLabel: Record<string, string> = {
  pending_approval: '변경 승인 대기',
  approved: '변경 재부팅 대기',
  applying: '변경 적용 중',
  applied: '변경 적용됨',
  rejected: '변경 반려',
  failed: '변경 실패',
  cancelled: '변경 취소',
}
export function changeWord(state: string): string {
  return changeLabel[state] ?? DASH
}
const changeFailure: Record<string, string> = {
  no_capacity: '자원이 부족해 변경을 적용하지 못했습니다.',
  guest_did_not_stop: 'VM 이 종료되지 않아 변경을 적용하지 못했습니다.',
  config_rejected: '변경을 적용하지 못했습니다.',
  guest_did_not_start: '변경은 적용됐지만 VM 이 켜지지 않았습니다.',
  lease_not_active: '사용 중인 VM 이 아니어서 변경을 적용하지 못했습니다.',
}
export function changeFailureSentence(code: string | null | undefined): string {
  return (code && changeFailure[code]) || '변경을 적용하지 못했습니다.'
}
/** 한 기계가 쥘 수 있는 가장 많은 장 수 — 스펙의 `gpus` 상한과 같다(`api/models.yaml`). */
export const MAX_GPUS = 8
/** 신청이 더할 수 있는 데이터 디스크의 수 — 스펙의 `volumes` 상한과 같다(`api/models.yaml`). */
export const MAX_VOLUMES = 4

/**
 * 목표 0 장의 낱말 — 쥔 카드가 있어도 없어도 `없음` 이다. 숫자 0 은 장 수를 고르다 만 것처럼 읽힌다.
 * 떼는 것은 `바뀌는 것` 의 `GPU A16 2장 → 없음` 이 말한다. 신청과 승인이 같은 낱말을 쓴다.
 */
export const GPU_NONE = '없음'

/**
 * 변경 한 줄: 적용된 뒤의 기계. `held` 는 지금 붙어 있는 장 수라, 카드가 하나도 없고
 * 생기지도 않는 변경은 GPU 를 말하지 않는다. 목표가 없는 옛 행은 더하는 장 수를 말한다.
 */
export function changeText(
  c: { target_cores: number; target_memory_mb: number; target_gpus?: number | null; gpus_add: number; gpu_model?: string | null },
  held: number,
): string {
  const parts = [`${c.target_cores}코어`, sizeMb(c.target_memory_mb)]
  const goal = c.target_gpus
  if (goal == null) {
    if (c.gpus_add > 0) parts.push(`GPU +${c.gpus_add}장${c.gpu_model ? ` ${c.gpu_model}` : ''}`)
  } else if (goal > 0) {
    parts.push(`GPU ${goal}장${c.gpu_model ? ` ${c.gpu_model}` : ''}`)
  } else if (held > 0) {
    parts.push(`GPU ${GPU_NONE}`)
  }
  return parts.join(' · ')
}

// VM 이름은 게스트의 hostname 이고 접속의 열쇠다 — 백엔드와 같은 규칙(`httpsrv.ValidLeaseName`).
// 어기면 VM 생성 페이지가 그 자리에서 이 문장을 보이고, 백엔드 거절(`invalid_name`)도 같은 문장이다.
const LEASE_NAME_RE = /^[a-z0-9]([a-z0-9-]{0,30}[a-z0-9])?$/
export const LEASE_NAME_RULE = 'VM 이름은 영문 소문자·숫자·하이픈(-)만, 32자까지 쓸 수 있습니다.'
export function leaseNameOk(name: string): boolean {
  return LEASE_NAME_RE.test(name)
}

/** 친 글자 중 쓸 수 있는 것만. 대문자는 버리지 않고 소문자로 바꾼다 — 전화 자판이 첫 글자를 대문자로 만든다. */
export function leaseNameTyped(raw: string): string {
  return raw.toLowerCase().replace(/[^a-z0-9-]/g, '')
}

/**
 * 변경·수정·승인 창의 `no_capacity`. 변경은 VM 이 있는 노드에서만 자원을 받으므로, 다른 창의 같은 사유
 * (배치할 자리가 없다 — 더 작은 사양이나 나중)와 할 수 있는 조치가 다르다.
 */
export const CHANGE_REASONS: Readonly<Record<string, string>> = { no_capacity: '이 VM 의 노드에 남은 자원이 모자랍니다.' }

/** 카드 묶음의 낱말 — `없음`·`A6000 2장`. 모델을 모르는 카드는 장 수만이다. */
export function cardsWord(gpus: number, model: string | null | undefined): string {
  if (gpus <= 0) return '없음'
  return model ? `${model} ${gpus}장` : `${gpus}장`
}

type SpecShape = { cores: number; memory_mb: number; gpus: number; gpu_model: string | null | undefined }

/**
 * 변경이 바꾸는 것, 한 줄에 하나 — `코어 4 → 8`·`메모리 16GB → 48GB`·`GPU 없음 → A6000 1장`. 같은 값은 줄이 없다.
 * 카드를 전부 떼는 목표는 숫자가 아니라 `없음` 이다(`GPU_NONE`). 변경 신청·사양 수정·변경 승인 창의 `바뀌는 것` 이 쓴다.
 */
export function changeLines(now: SpecShape, to: SpecShape): string[] {
  const out: string[] = []
  if (to.cores !== now.cores) out.push(`코어 ${now.cores} → ${to.cores}`)
  if (to.memory_mb !== now.memory_mb) out.push(`메모리 ${sizeMb(now.memory_mb)} → ${sizeMb(to.memory_mb)}`)
  if (to.gpus !== now.gpus || (to.gpus > 0 && (to.gpu_model ?? null) !== (now.gpu_model ?? null))) {
    out.push(`GPU ${cardsWord(now.gpus, now.gpu_model)} → ${to.gpus === 0 ? GPU_NONE : cardsWord(to.gpus, to.gpu_model)}`)
  }
  return out
}
